import { useState } from "react";
import { Phone, Mail, MapPin, Send, Plane } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/analytics";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      track("lead", { meta: { kind: "contact" } });
      toast.success(t("contact.sentToast"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white min-h-screen pt-32 pb-24 px-5 sm:px-8">
      <Seo
        title="Contact Belo Rentals | Boston & Logan Airport Car Rental"
        description="Contact Belo Rentals for car rental in Boston with Logan Airport pickup & drop-off. Call 978-221-1451 or message us. Locations in Leominster, Malden and Clinton, MA."
        path="/contact"
        keywords="contact car rental Boston, Logan Airport car rental phone, Belo Rentals Malden, car rental Clinton MA"
      />
      <div className="max-w-6xl mx-auto">
        <p className="label-caps text-blue-600">{t("contact.label")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 mt-3 tracking-tight font-bold">{t("contact.title")}</h1>
        <p className="text-slate-500 mt-4 max-w-xl">{t("contact.subtitle")}</p>
        <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
          Belo Rentals is a Boston-based car rental company serving travelers across Massachusetts, with convenient
          Logan Airport (BOS) pickup &amp; drop-off and locations in Malden and Clinton. Whether you're booking a car
          for an airport arrival, a weekend on Cape Cod, or a longer stay in the city, our local team is here to help —
          call, email, or send us a message and we'll get right back to you.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
          <div className="space-y-4">
            <ContactRow icon={Phone} label={t("contact.phone")} value="978-221-1451" href="tel:9782211451" testid="contact-phone" />
            <ContactRow icon={Mail} label={t("contact.email")} value="Rentals@belorentals.com" href="mailto:Rentals@belorentals.com" testid="contact-email" />
            <ContactRow icon={MapPin} label={t("contact.maldenLocation")} value="235 Washington St, Malden, MA 02148" testid="contact-address" />
            <ContactRow icon={MapPin} label={t("contact.clintonLocation")} value="291 Berlin St, Clinton, MA 01510" testid="contact-address-2" />
            <div className="flex items-start gap-4 bg-blue-50 rounded-2xl p-6">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-amber-500 text-white shrink-0"><Plane size={20} /></span>
              <div>
                <p className="label-caps text-blue-600">{t("contact.airportService")}</p>
                <p className="text-slate-900 font-medium mt-1">{t("contact.airportAvailable")}</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="bg-slate-50 rounded-2xl p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("contact.name")}</span>
                <input required value={form.name} onChange={set("name")} data-testid="contact-input-name" className="belo-input" placeholder={t("contact.namePlaceholder")} />
              </div>
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("contact.phone")}</span>
                <input value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" className="belo-input" placeholder={t("contact.phonePlaceholder")} />
              </div>
            </div>
            <div className="mt-5">
              <span className="label-caps text-slate-400 block mb-2">{t("contact.email")}</span>
              <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-input-email" className="belo-input" placeholder={t("contact.emailPlaceholder")} />
            </div>
            <div className="mt-5">
              <span className="label-caps text-slate-400 block mb-2">{t("contact.message")}</span>
              <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-input-message" className="belo-input resize-none" placeholder={t("contact.messagePlaceholder")} />
            </div>
            <button type="submit" disabled={submitting} data-testid="contact-submit" className="mt-6 inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-4 rounded-full font-semibold hover:bg-blue-700 transition-all disabled:opacity-50">
              <Send size={16} /> {submitting ? t("contact.sending") : t("contact.sendMessage")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-card p-6 flex items-start gap-4 hover:shadow-card-hover transition-shadow">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white shrink-0"><Icon size={20} /></span>
      <div>
        <p className="label-caps text-slate-400">{label}</p>
        <p className="text-slate-900 font-medium mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? <a data-testid={testid} href={href} className="block">{inner}</a> : <div data-testid={testid}>{inner}</div>;
};
